import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { ArrowForward, ArrowForwardSuccess } from '@yme/assets/icons';
import { Alert, AlertType } from '@yme/atoms/alert';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { ModalWindowFooter } from '@yme/atoms/modal-footer';
import { MD_MODAL, StaticModalWindow } from '@yme/atoms/modal-window';
import { CERTIFICATE_CODES, REDEMPTION_STATUS, } from '@yme/components/CheckoutForm/PaymentForm/PaymentOptions/CycleSchemeOption';
import { TransformedTextToReactNode } from '@yme/components/CheckoutForm/PaymentForm/PaymentOptions/TransformedTextToReactNode';
import { resetRedemptionState, setHasRedemptionError, setRedemptionCode, setRedemptionValidationCodeResult, } from '@yme/components/CheckoutForm/PaymentForm/PaymentOptions/redux/actions';
import { getCycleSchemeState, getHasRedemptionError, getRedemptionValidationResult, } from '@yme/components/CheckoutForm/PaymentForm/PaymentOptions/redux/selectors';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { setIsModalHide } from '@yme/modals/actions';
import { getModalData } from '@yme/modals/selectors';
import { FormField, FormFieldType } from '@yme/molecules/form-field';
import { getRTL } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreBlack300 } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { INDENT } from '@yme/tokens/sizes';
import { RegularTextLevel3 } from '@yme/tokens/typography';
import { regExpRedemptionCode } from '@yme/utils/regEx';
import { REDEMPTION_ALERT_ERROR_MESSAGE, REDEMPTION_CODE_INPUT_FIELD, VALIDATION_LENGTH_MESSAGE, VALIDATION_REQUIRED_MESSAGE, ModalSeparatorStyled, REDEMPTION_CODE_INPUT_PLACEHOLDER, REDEMPTION_CODE_INPUT_LABEL, } from './';
export const RedemptionForm = ({ handleSubmit, values, isValid, setErrors, errors, isSubmitting, setSubmitting, }) => {
    const isRightToLeft = useSelector(getRTL);
    const redemptionValidationResult = useSelector(getRedemptionValidationResult);
    const { infoText, title } = useSelector(getModalData);
    const hasRedemptionError = useSelector(getHasRedemptionError);
    const dispatch = useDispatch();
    const { isInfiniteDesktopView } = useWindowSize();
    const { t } = useTranslation();
    const [isBottomSheetOpened, setIsBottomSheetOpened] = useState(true);
    const isFailed = redemptionValidationResult === REDEMPTION_STATUS.failed;
    useEffect(() => {
        if (isFailed && hasRedemptionError) {
            setErrors({
                ...errors,
                [REDEMPTION_CODE_INPUT_FIELD]: CERTIFICATE_CODES.invalid,
            });
            dispatch(setRedemptionValidationCodeResult(''));
            setSubmitting(false);
        }
    }, [
        dispatch,
        errors,
        hasRedemptionError,
        isFailed,
        redemptionValidationResult,
        setErrors,
        setSubmitting,
    ]);
    useEffect(() => {
        if (!errors.redemptionCode?.length && !hasRedemptionError) {
            dispatch(setRedemptionCode(values.redemptionCode));
        }
    }, [dispatch, errors.redemptionCode?.length, hasRedemptionError, values.redemptionCode]);
    useEffect(() => {
        dispatch(setHasRedemptionError(!isValid));
    }, [dispatch, isValid]);
    const handleHideModal = useCallback(() => {
        dispatch(resetRedemptionState({
            redemptionCode: '',
            redemptionValidationResult: '',
        }));
        if (isInfiniteDesktopView) {
            dispatch(setIsModalHide());
        }
        else {
            setIsBottomSheetOpened(false);
        }
    }, [dispatch, isInfiniteDesktopView]);
    const handleBottomSheetSpringEnd = useCallback(({ type }) => {
        if (type === 'CLOSE') {
            dispatch(setIsModalHide());
        }
    }, [dispatch]);
    const handledRenderMessageCallback = useCallback((value) => {
        const containsEmailPlaceholder = /\{email}/.test(value);
        if (containsEmailPlaceholder) {
            return React.createElement(TransformedTextToReactNode, { text: value });
        }
        return value;
    }, []);
    const modalContent = (React.createElement(FlexWrapper, { gap: "24px", flexDirection: "column" },
        infoText && React.createElement(RegularTextLevel3, { color: colorCoreBlack300 }, infoText),
        React.createElement("form", { onSubmit: handleSubmit },
            React.createElement(FormField, { isRequired: true, isFullWidth: true, isRightToLeft: isRightToLeft, fieldType: FormFieldType.INPUT, name: REDEMPTION_CODE_INPUT_FIELD, id: REDEMPTION_CODE_INPUT_FIELD, label: REDEMPTION_CODE_INPUT_LABEL, placeholder: REDEMPTION_CODE_INPUT_PLACEHOLDER, dataTestId: "redemptionCode-code-input", value: values.redemptionCode, renderMessageCallback: handledRenderMessageCallback, isDisabled: isSubmitting })),
        React.createElement(ModalSeparatorStyled, null),
        React.createElement(Alert, { type: AlertType.ERROR, message: REDEMPTION_ALERT_ERROR_MESSAGE })));
    const modalFooter = useMemo(() => {
        return (React.createElement(ModalWindowFooter, { isSubmitButton: true, isCancelButton: true, isColorIcon: true, submitButtonIcon: isValid ? ArrowForwardSuccess : ArrowForward, cancelButtonLabel: t('common_cancel_button'), submitButtonLabel: title, isSubmitButtonFullWidth: !isInfiniteDesktopView, isHiddenBorder: !isInfiniteDesktopView, onSubmit: handleSubmit, onCancel: handleHideModal, isSubmitButtonDisabled: !isValid || isSubmitting }));
    }, [handleHideModal, handleSubmit, isInfiniteDesktopView, isSubmitting, isValid, t, title]);
    if (isInfiniteDesktopView) {
        return (React.createElement(StaticModalWindow, { onHide: handleHideModal, minHeight: "auto", staticModalSize: MD_MODAL, title: title, isRightToLeft: isRightToLeft, footerComponent: modalFooter }, modalContent));
    }
    return (React.createElement(BottomSheet, { initialFocusRef: false, open: isBottomSheetOpened, header: React.createElement(Header, { isRightToLeft: isRightToLeft, headerLeft: title }), footer: modalFooter, onDismiss: handleHideModal, onSpringEnd: handleBottomSheetSpringEnd },
        React.createElement(FlexWrapper, { flexDirection: "column", padding: INDENT.xs }, modalContent)));
};
export const RedemptionCodeModal = () => {
    const { onHandleSubmit } = useSelector(getModalData);
    const initialValues = {
        [REDEMPTION_CODE_INPUT_FIELD]: '',
    };
    const { redemptionCode, certificateCode } = useSelector(getCycleSchemeState);
    const validationSchema = Yup.object().shape({
        [REDEMPTION_CODE_INPUT_FIELD]: Yup.string()
            .trim()
            .required(VALIDATION_REQUIRED_MESSAGE)
            .matches(regExpRedemptionCode, {
            message: VALIDATION_LENGTH_MESSAGE,
            excludeEmptyString: true,
        }),
    });
    return (React.createElement(Formik, { validateOnBlur: true, validateOnMount: true, validateOnChange: true, validationSchema: validationSchema, initialValues: initialValues, onSubmit: () => onHandleSubmit?.({
            redemptionCode,
            certificateCode,
        }), component: RedemptionForm }));
};
